import React from 'react';
import { Box, IconButton, Typography, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

function Section({ title, children }) {
	return (
		<Box sx={{ px: 2, py: 1.5 }}>
			<Typography variant="overline" color="text.disabled" sx={{ fontSize: 10, letterSpacing: 1 }}>
				{title}
			</Typography>
			<Box sx={{ mt: 0.5 }}>
				{children}
			</Box>
		</Box>
	);
}

export default function ChatSettingsOverlay({ onClose, context, skills, displayStyle, displayStyleName, profileName }) {
	return (
		<Box sx={{
			position: 'absolute',
			inset: 0,
			zIndex: 10,
			bgcolor: 'background.paper',
			overflowY: 'auto',
			display: 'flex',
			flexDirection: 'column',
		}}>
			{/* Sticky header */}
			<Box sx={{
				position: 'sticky',
				top: 0,
				zIndex: 1,
				bgcolor: 'background.paper',
				display: 'flex',
				alignItems: 'center',
				px: 2,
				py: 1,
				borderBottom: '1px solid',
				borderColor: 'divider',
			}}>
				<Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 600 }}>
					Chat Settings
				</Typography>
				<IconButton size="small" onClick={onClose} aria-label="Close settings">
					<CloseIcon fontSize="small" />
				</IconButton>
			</Box>

			{/* Catalyst Profile section */}
			{profileName && (
				<>
					<Section title="Catalyst Profile">
						<Typography variant="body2" color="text.secondary">{profileName}</Typography>
					</Section>
					<Divider />
				</>
			)}

			{/* Context section */}
			<Section title="Context">
				{context?.reportName ? (
					<Typography variant="body2">
						{context.reportName}{context.activeTab?.name ? ` · ${context.activeTab.name}` : ''}
					</Typography>
				) : (
					<Typography variant="body2" color="text.disabled">
						No active context
					</Typography>
				)}
			</Section>

			<Divider />

			{/* Skills section */}
			<Section title="Skills">
				{skills && skills.length > 0 ? (
					skills.map((skill) => (
						<Box key={skill.id} sx={{ mb: 1.5 }}>
							<Typography variant="body2" sx={{ fontWeight: 600 }}>
								✨ {skill.name}
							</Typography>
							{skill.description && (
								<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
									{skill.description}
								</Typography>
							)}
						</Box>
					))
				) : (
					<Typography variant="body2" color="text.disabled">
						No skills active for this profile
					</Typography>
				)}
			</Section>

			<Divider />

			{/* Display Style section */}
			<Section title="Display Style">
				{displayStyle ? (
					<>
						{displayStyleName && (
							<Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500 }}>
								{displayStyleName}
							</Typography>
						)}
						{displayStyle.chartColors?.length > 0 && (
							<Box sx={{ display: 'flex', gap: 0.5, mb: 0.75, flexWrap: 'wrap' }}>
								{displayStyle.chartColors.slice(0, 8).map((color, i) => (
									<Box
										key={i}
										title={color}
										sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: color, flexShrink: 0 }}
									/>
								))}
							</Box>
						)}
						{displayStyle.numberFormat && (
							<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
								Numbers: {displayStyle.numberFormat.charAt(0).toUpperCase() + displayStyle.numberFormat.slice(1)}
							</Typography>
						)}
						{displayStyle.accentColor && (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
								<Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: displayStyle.accentColor, flexShrink: 0 }} />
								<Typography variant="caption" color="text.secondary">Accent</Typography>
							</Box>
						)}
					</>
				) : (
					<Typography variant="body2" color="text.disabled">
						Default display style
					</Typography>
				)}
			</Section>

		</Box>
	);
}
